import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleLogout } from '../Modules/Common/Auth';
import { setModal } from '../Modules/Common/App';
import { LogoutModal } from '../Modules/Common/Auth/Components/Modals/LogoutModal';
import { useSettings } from './useSettings';

const useLogout = () => {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
    const { getSettingValue } = useSettings();
    const timerId = useRef(null);
    const resetTimerRef = useRef(() => {});

    const logoutDurationMinutes = parseInt(getSettingValue('auto-logout-duration', 5), 10) || 5;
    const warningDurationSeconds = parseInt(getSettingValue('auto-logout-warning-duration', 60), 10) || 60;
    const logoutDurationInSeconds = logoutDurationMinutes * 60;

    const logout = useCallback(async () => {
        dispatch(setModal({ component: null, open: false }));
        await handleLogout(dispatch);
    }, [dispatch]);

    const showLogoutWarningModal = useCallback(() => {
        dispatch(setModal({
            open: true,
            size: 'xs',
            component: (
                <LogoutModal
                    onLogout={logout}
                    timerDuration={warningDurationSeconds}
                    onStayLoggedIn={() => {
                        dispatch(setModal({ component: null, open: false }));
                        resetTimerRef.current();
                    }}
                />
            ),
        }));
    }, [dispatch, logout, warningDurationSeconds]);

    const resetTimer = useCallback(() => {
        if (timerId.current) {
            clearTimeout(timerId.current);
        }

        if (logoutDurationInSeconds <= 0) {
            return;
        }

        timerId.current = setTimeout(() => {
            showLogoutWarningModal();
        }, logoutDurationInSeconds * 1000);
    }, [logoutDurationInSeconds, showLogoutWarningModal]);

    resetTimerRef.current = resetTimer;

    const handleUserActivity = useCallback(() => {
        if (isLoggedIn) {
            resetTimer();
        }
    }, [isLoggedIn, resetTimer]);

    useEffect(() => {
        if (!isLoggedIn) {
            return undefined;
        }

        const events = ['mousemove', 'keypress', 'click', 'scroll', 'touchstart'];
        events.forEach((event) => window.addEventListener(event, handleUserActivity));
        resetTimer();

        return () => {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
            events.forEach((event) => window.removeEventListener(event, handleUserActivity));
        };
    }, [isLoggedIn, handleUserActivity, resetTimer]);
};

export default useLogout;
